import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { UserCircleIcon, HeartIcon, ArrowLeftIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import Turnstile from "react-turnstile";

const brandColor = '#e46809';
const brandColorHover = '#d15e08';

// HELPER COMPONENTS
const FormInput = ({ id, label, type = 'text', placeholder, ...props }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <input
      type={type} id={id} name={id} placeholder={placeholder} required
      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
      {...props}
    />
  </div>
);

const PhoneInput = ({ id, label, value, onChange }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <div className="flex">
      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm dark:bg-gray-600 dark:border-gray-600 dark:text-gray-400">
        +234
      </span>
      <input
        type="tel" id={id} name="phoneNumber" value={value} onChange={onChange} required
        placeholder="801 234 5678"
        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
      />
    </div>
  </div>
);

const RoleButton = ({ role, currentRole, setRole, Icon, title, subtitle }) => (
  <button type="button" onClick={() => setRole(role)} className={`flex flex-col items-center justify-center text-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 h-full w-full ${currentRole === role ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/50 shadow-lg' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`}>
    <Icon className={`w-8 h-8 mb-2 flex-shrink-0 ${currentRole === role ? 'text-orange-600' : 'text-gray-500'}`} />
    <span className="font-bold text-gray-800 dark:text-gray-200">{title}</span>
    <span className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>
  </button>
);

// Custom Google Button Component
const CustomGoogleButton = ({ onClick, disabled, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="flex items-center justify-center w-full py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" width="24" height="24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    {children}
  </button>
);

// FORM COMPONENTS
const SignInForm = () => {
  const { login, loginWithToken } = useAuth();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!turnstileToken) {
      setError("Please wait for CAPTCHA verification and try again.");
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email');
    const password = formData.get('password');
    try {
      await login(email, password, turnstileToken);
    } catch (err) {
      setError(err.message || "Failed to sign in. Please check your credentials.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async (googleCredential) => {
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: googleCredential, role: 'donor' })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      loginWithToken(data.token, data.user);
    } catch (err) {
      setError(err.message || 'Google sign in failed.');
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput id="email" label="Email Address" type="email" placeholder="you@example.com" />
        <FormInput id="password" label="Password" type="password" placeholder="••••••••" />
        <div className="flex justify-center">
          <Turnstile sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY} onVerify={(token) => setTurnstileToken(token)} />
        </div>
        {error && <p className="text-sm font-semibold text-red-600 text-center">{error}</p>}
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm font-medium text-orange-600 hover:text-orange-500">Forgot password?</Link>
        </div>
        <button type="submit" disabled={isSubmitting || !turnstileToken} className="w-full py-3 px-4 text-lg font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: brandColor }} onMouseOver={e => e.currentTarget.style.backgroundColor = brandColorHover} onMouseOut={e => e.currentTarget.style.backgroundColor = brandColor}>
          {isSubmitting ? 'Signing In...' : 'Sign In'}
        </button>
      </form>
      <div className="relative flex py-2 items-center">
        <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
        <span className="flex-shrink mx-4 text-gray-400">OR</span>
        <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
      </div>
      <div className="flex justify-center">
        <GoogleLogin
          onSuccess={credentialResponse => handleGoogleAuth(credentialResponse.credential)}
          onError={() => setError('Google Sign In failed. Please try again.')}
          useOneTap
          ux_mode="popup"
          clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          render={(renderProps) => (
            <CustomGoogleButton 
              onClick={renderProps.onClick} 
              disabled={renderProps.disabled}
            >
              Sign in with Google
            </CustomGoogleButton>
          )}
        />
      </div>
    </div>
  );
};

const SignUpForm = ({ initialRole, setRegistrationEmail, setIsVerificationNeeded }) => {
  const { loginWithToken } = useAuth();
  const [userRole, setUserRole] = useState(initialRole || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [organizationName, setOrganizationName] = useState('');

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (!userRole) { setError("Please select a role (Recipient, Organization, or Donor)."); return; }
    if (!agreedToTerms) { setError("You must agree to the Terms and Privacy Policy."); return; }
    if (!turnstileToken) { setError("CAPTCHA verification is required."); return; }

    // Additional validation for organizations
    if (userRole === 'organization' && !organizationName.trim()) {
      setError("Organization name is required.");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const firstName = formData.get('firstName');
    const lastName = formData.get('lastName');
    const email = formData.get('email');
    const password = formData.get('password');
    const fullPhoneNumber = `+234${phoneNumber}`;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phoneNumber: fullPhoneNumber,
          password,
          role: userRole,
          turnstileToken,
          organizationName: userRole === 'organization' ? organizationName : undefined,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          // User exists - check if verified or not
          const userExistsResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-exists?email=${encodeURIComponent(email)}`);
          const userExistsData = await userExistsResponse.json();
          
          if (userExistsData.exists && !userExistsData.isVerified) {
            // User exists but is not verified - redirect to verification
            setRegistrationEmail(email);
            setIsVerificationNeeded(true);
            setError('');
          } else {
            setError(data.message || 'An account with this email already exists.');
          }
        } else {
          setError(data.message || 'Failed to create account.');
        }
      } else {
        // Registration successful - redirect to verification
        setRegistrationEmail(email);
        setIsVerificationNeeded(true);
      }
    } catch (err) {
      setError(err.message || 'An error occurred during registration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async (googleToken, role) => {
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: googleToken, role: role })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      loginWithToken(data.token, data.user);
    } catch (err) {
      setError(err.message || 'Google sign up failed.');
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSignUp} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">I am here to...</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <RoleButton role="recipient" currentRole={userRole} setRole={setUserRole} Icon={UserCircleIcon} title="Start a Campaign" subtitle="I need a device." />
            <RoleButton role="organization" currentRole={userRole} setRole={setUserRole} Icon={BuildingOfficeIcon} title="Organization" subtitle="We need devices." />
            <RoleButton role="donor" currentRole={userRole} setRole={setUserRole} Icon={HeartIcon} title="Support a Campaign" subtitle="I want to give." />
          </div>
        </div>
        {userRole && (
          <div className="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            {/* Organization Name Field - Only shown for organizations */}
            {userRole === 'organization' && (
              <FormInput 
                id="organizationName" 
                label="Organization Name" 
                placeholder="Enter your organization name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
              />
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput id="firstName" label="First Name" placeholder="First Name" />
              <FormInput id="lastName" label="Last Name" placeholder="Last Name" />
            </div>
            <FormInput id="email" label="Email Address" type="email" placeholder="you@example.com" />
            <PhoneInput id="phoneNumber" label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
            <FormInput id="password" label="Password" type="password" placeholder="Minimum 8 characters" />
            <div className="flex justify-center">
              <Turnstile sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY} onVerify={(token) => setTurnstileToken(token)} />
            </div>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input id="terms" name="terms" type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded" />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="font-medium text-gray-700 dark:text-gray-300">
                  I agree to the{' '}
                  <Link to="/terms-of-service" target="_blank" className="text-orange-600 hover:underline">Terms</Link>
                  {' & '}
                  <Link to="/privacy-policy" target="_blank" className="text-orange-600 hover:underline">Privacy Policy</Link>
                </label>
              </div>
            </div>
            {error && <p className="text-sm font-semibold text-red-600 text-center">{error}</p>}
            <button
              type="submit"
              disabled={isSubmitting || !turnstileToken || !agreedToTerms}
              className="w-full py-3 px-4 text-lg font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brandColor }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = brandColorHover}
              onMouseOut={e => e.currentTarget.style.backgroundColor = brandColor}
            >
              {isSubmitting ? 'Creating Account...' : 'Create My Account'}
            </button>
          </div>
        )}
      </form>
      {userRole === 'donor' && (
        <>
          <div className="relative flex py-2 items-center">
            <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
            <span className="flex-shrink mx-4 text-gray-400">OR</span>
            <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
          </div>
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={credentialResponse => handleGoogleAuth(credentialResponse.credential, userRole)}
              onError={() => setError('Google Sign Up failed. Please try again.')}
              useOneTap
              ux_mode="popup"
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
              render={(renderProps) => (
                <CustomGoogleButton 
                  onClick={renderProps.onClick} 
                  disabled={renderProps.disabled}
                >
                  Sign up with Google
                </CustomGoogleButton>
              )}
            />
          </div>
        </>
      )}
      {(userRole === 'recipient' || userRole === 'organization') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800 mt-4">
          <p className="text-sm text-blue-700 dark:text-blue-300 text-center">
            <strong>Note:</strong> {userRole === 'recipient' ? 'Recipients' : 'Organizations'} must create an account using the form above. 
            Google sign-in is only available for donors.
          </p>
        </div>
      )}
    </div>
  );
};

const VerificationForm = ({ email, onBack }) => {
  const { loginWithToken } = useAuth();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let intervalId;
    if (resendTimer > 0) {
      intervalId = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else {
      clearInterval(intervalId);
      setCanResend(true);
    }
    return () => clearInterval(intervalId);
  }, [resendTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const token = formData.get('verificationCode');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Verification failed.');
      loginWithToken(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setCanResend(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend OTP.');
      }
      setResendTimer(60);
    } catch (err) {
      setError(err.message);
      setCanResend(true);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Check Your Email</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          We've sent a 6-digit verification code to <span className="font-medium">{email}</span>.
        </p>
        <button onClick={onBack} className="mt-2 text-sm text-orange-600 hover:underline">Go back</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInput id="verificationCode" label="Verification Code" placeholder="123456" />
        {error && <p className="text-sm font-semibold text-red-600 text-center">{error}</p>}
        <button type="submit" disabled={isSubmitting} className="w-full py-3 px-4 text-lg font-semibold text-white rounded-lg" style={{ backgroundColor: brandColor }} onMouseOver={e => e.currentTarget.style.backgroundColor = brandColorHover} onMouseOut={e => e.currentTarget.style.backgroundColor = brandColor}>
          {isSubmitting ? 'Verifying...' : 'Verify Account'}
        </button>
      </form>
      <div className="text-center text-sm">
        <p className="text-gray-500 dark:text-gray-400">
          Didn't receive the email?{' '}
          {canResend ? (
            <button type="button" onClick={handleResend} className="font-medium text-orange-600 hover:text-orange-500">Resend Code</button>
          ) : (
            <span>Resending in {resendTimer} seconds</span>
          )}
        </p>
      </div>
    </div>
  );
};

// MAIN PAGE COMPONENT
export default function AuthPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('signin');
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [initialRole, setInitialRole] = useState('');
  const [isVerificationNeeded, setIsVerificationNeeded] = useState(false);

  useEffect(() => {
    if (location.state?.defaultTab === 'signup') {
      setActiveTab('signup');
      if (location.state?.role) { setInitialRole(location.state.role); }
    }
  }, [location.state]);

  const handleBackToRegister = () => {
    setIsVerificationNeeded(false);
    setRegistrationEmail('');
  };

  const renderSignUpContent = () => {
    if (isVerificationNeeded) {
      return <VerificationForm email={registrationEmail} onBack={handleBackToRegister} />;
    }
    return <SignUpForm
      initialRole={initialRole}
      setRegistrationEmail={setRegistrationEmail}
      setIsVerificationNeeded={setIsVerificationNeeded}
    />;
  };

  const TabButton = ({ tabName, children }) => (
    <button onClick={() => { setActiveTab(tabName); setIsVerificationNeeded(false); }} className={`w-1/2 py-4 text-center font-bold text-lg border-b-4 transition-colors ${activeTab === tabName ? 'border-orange-500 text-orange-600 dark:text-orange-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
      {children}
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-orange-600 dark:text-gray-400 dark:hover:text-orange-400 transition-colors">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-2xl overflow-hidden">
          <div className="flex">
            <TabButton tabName="signin">Sign In</TabButton>
            <TabButton tabName="signup">Create Account</TabButton>
          </div>
          <div className="p-8">
            {activeTab === 'signin' ? <SignInForm /> : renderSignUpContent()}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {activeTab === 'signin' ? "Don't have an account?" : 'Already have an account?'}
                <button onClick={() => setActiveTab(activeTab === 'signin' ? 'signup' : 'signin')} className="font-medium text-orange-600 hover:text-orange-500 ml-1">
                  {activeTab === 'signin' ? 'Create one' : 'Sign In'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}